import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, Input, InputNumber, Select, Button, Card, Row, Col,
  message, Space, Typography, DatePicker,
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../api/axios';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const SatilikForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [emlakTipleri, setEmlakTipleri] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const fetchIlceler = async (plakaNo) => {
    if (!plakaNo) { setIlceler([]); return; }
    try {
      const res = await api.get(`/lookup/ilceler/${plakaNo}`);
      setIlceler(Array.isArray(res.data) ? res.data : res.data.data || []);
    } catch { setIlceler([]); }
  };

  // Tüm lookup'lar ve form verisi tek seferde yükleniyor.
  // React 18 bu fonksiyon içindeki tüm setState çağrılarını tek render'a toplar,
  // böylece Select hem seçeneklere hem değere aynı render'da sahip olur.
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        api.get('/lookup/iller'),
        api.get('/lookup/emlaktipi'),
        ...(isEdit ? [api.get(`/satilik/${id}`)] : []),
      ];
      const results = await Promise.allSettled(requests);
      const [illerRes, emlakRes, dataRes] = results;

      if (illerRes.status === 'fulfilled') {
        setIller(Array.isArray(illerRes.value.data) ? illerRes.value.data : illerRes.value.data.data || []);
      }
      if (emlakRes.status === 'fulfilled') {
        setEmlakTipleri(Array.isArray(emlakRes.value.data) ? emlakRes.value.data : emlakRes.value.data.data || []);
      }
      if (isEdit && dataRes) {
        if (dataRes.status === 'fulfilled') {
          const item = dataRes.value.data.data || dataRes.value.data;
          if (item.taksitBaslangicTarihi) item.taksitBaslangicTarihi = dayjs(item.taksitBaslangicTarihi);
          if (item.plakaNo) fetchIlceler(item.plakaNo);
          if (item.emlakTipiId) item.emlakTipiId = Number(item.emlakTipiId);
          form.setFieldsValue(item);
        } else {
          message.error('Kayıt yüklenemedi');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, form]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleIlChange = (val) => {
    form.setFieldValue('ilceId', undefined);
    fetchIlceler(val);
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const payload = {
        emlakTipiId: values.emlakTipiId,
        plakaNo: values.plakaNo,
        ilceId: values.ilceId,
        mevki: values.mevki,
        adaNo: values.adaNo,
        parselNo: values.parselNo,
        daireNo: values.daireNo,
        nevi: values.nevi,
        adres: values.adres,
        saticiAdi: values.saticiAdi,
        saticiTc: values.saticiTc,
        komisyoncuAdi: values.komisyoncuAdi,
        vergiDairesi: values.vergiDairesi,
        vergiNo: values.vergiNo,
        aliciAdi: values.aliciAdi,
        aliciTc: values.aliciTc,
        anapara: values.anapara,
        pesinat: values.pesinat,
        dbKredi: values.dbKredi,
        kapora: values.kapora,
        kalan: values.kalan,
        taksitSayisi: values.taksitSayisi,
        taksitTutari: values.taksitTutari,
        taksitBaslangicTarihi: values.taksitBaslangicTarihi ? values.taksitBaslangicTarihi.toISOString() : null,
      };
      if (isEdit) {
        await api.put(`/satilik/${id}`, payload);
        message.success('Satılık işlem güncellendi');
      } else {
        await api.post('/satilik', payload);
        message.success('Satılık işlem eklendi');
      }
      navigate('/satilik');
    } catch (err) {
      message.error(err.response?.data?.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const sectionStyle = { marginBottom: 16, borderRadius: 8 };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/satilik')}>Geri</Button>
            <Title level={4} style={{ margin: 0 }}>{isEdit ? 'Satılık Düzenle' : 'Yeni Satılık Ekle'}</Title>
          </Space>
        </Col>
      </Row>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        {/* Mülk Bilgileri */}
        <Card title="Mülk Bilgileri" style={sectionStyle} loading={loading}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="emlakTipiId" label="Emlak Tipi">
                <Select placeholder="Emlak tipi seçin" showSearch allowClear>
                  {emlakTipleri.map((t) => (
                    <Option key={t.emlakTipiId} value={Number(t.emlakTipiId)}>{t.emlakTipiAdi}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="plakaNo" label="İl">
                <Select
                  placeholder="İl seçin"
                  showSearch
                  allowClear
                  onChange={handleIlChange}
                  filterOption={(input, option) =>
                    (option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {iller.map((il) => (
                    <Option key={il.plakaNo} value={il.plakaNo}>
                      {il.ilAdi}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="ilceId" label="İlçe">
                <Select placeholder="İlçe seçin" showSearch allowClear disabled={!ilceler.length}>
                  {ilceler.map((ilce) => (
                    <Option key={ilce.ilceId} value={ilce.ilceId}>{ilce.ilceAdi}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item name="mevki" label="Mevki">
                <Input placeholder="Mevki" />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="adaNo" label="Ada No">
                <Input placeholder="Ada No" />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="parselNo" label="Parsel No">
                <Input placeholder="Parsel No" />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="daireNo" label="Daire No">
                <Input placeholder="Daire No" />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="nevi" label="Nevi">
                <Input placeholder="Nevi" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="adres" label="Adres">
                <TextArea rows={2} placeholder="Tam adres" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Satıcı Bilgileri */}
        <Card title="Satıcı Bilgileri" style={sectionStyle}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="saticiAdi" label="Satıcı Adı" rules={[{ required: true, message: 'Zorunlu' }]}>
                <Input placeholder="Satıcı adı soyadı" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="saticiTc" label="TC Kimlik No">
                <Input placeholder="TC Kimlik No" maxLength={11} />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item name="komisyoncuAdi" label="Komisyoncu Adı">
                <Input placeholder="Komisyoncu adı" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="vergiDairesi" label="Vergi Dairesi">
                <Input placeholder="Vergi dairesi" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="vergiNo" label="Vergi No">
                <Input placeholder="Vergi no" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Alıcı Bilgileri */}
        <Card title="Alıcı Bilgileri" style={sectionStyle}>
          <Row gutter={16}>
            <Col xs={24} md={10}>
              <Form.Item name="aliciAdi" label="Alıcı Adı" rules={[{ required: true, message: 'Zorunlu' }]}>
                <Input placeholder="Alıcı adı soyadı" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="aliciTc" label="TC Kimlik No">
                <Input placeholder="TC Kimlik No" maxLength={11} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Finansal Bilgiler */}
        <Card title="Finansal Bilgiler" style={sectionStyle}>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item name="anapara" label="Ana Para (₺)">
                <InputNumber
                  min={0} style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => v.replace(/\./g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="pesinat" label="Peşinat (₺)">
                <InputNumber
                  min={0} style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => v.replace(/\./g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="dbKredi" label="Banka Kredisi (₺)">
                <InputNumber
                  min={0} style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => v.replace(/\./g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="kapora" label="Kapora (₺)">
                <InputNumber
                  min={0} style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => v.replace(/\./g, '')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item name="kalan" label="Kalan (₺)">
                <InputNumber
                  min={0} style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => v.replace(/\./g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="taksitSayisi" label="Taksit Sayısı">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="taksitTutari" label="Taksit Tutarı (₺)">
                <InputNumber
                  min={0} style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => v.replace(/\./g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="taksitBaslangicTarihi" label="Taksit Başlangıç Tarihi">
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Row justify="end" style={{ marginTop: 8 }}>
          <Space>
            <Button onClick={() => navigate('/satilik')}>İptal</Button>
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
              {isEdit ? 'Güncelle' : 'Kaydet'}
            </Button>
          </Space>
        </Row>
      </Form>
    </div>
  );
};

export default SatilikForm;
